/**
 * Summary field transforms (spec 02 §2.3).
 *
 * Pure functions so the toolbar, shortcuts, Enter and paste can be tested
 * without a browser parser. The composer applies the result with
 * document.execCommand("insertText") so one undo step reverses each action.
 */

export const SUMMARY_LIMIT = 4000;

export interface SummaryEdit {
  value: string;
  selStart: number;
  selEnd: number;
}

export interface TextReplacement {
  start: number;
  end: number;
  text: string;
}

const BULLET_LINE = /^(\s*)[-+*]\s+(.*)$/;
const NUMBER_LINE = /^(\s*)(\d+)([.)])\s+(.*)$/;
const LIST_LINE = /^(\s*)(?:[-+*]|\d+[.)])\s+/;

/** The slice of `prev` that `next` replaces. Used so insertText stays one undo step. */
export function changedRange(prev: string, next: string): TextReplacement {
  let start = 0;
  const shared = Math.min(prev.length, next.length);
  while (start < shared && prev[start] === next[start]) start += 1;
  let endPrev = prev.length;
  let endNext = next.length;
  while (endPrev > start && endNext > start && prev[endPrev - 1] === next[endNext - 1]) {
    endPrev -= 1;
    endNext -= 1;
  }
  return { start, end: endPrev, text: next.slice(start, endNext) };
}

/**
 * Apply an edit on the live textarea. insertText keeps the native undo stack.
 * setRangeText is the fallback when the browser refuses the command.
 */
export function applySummaryEdit(textarea: HTMLTextAreaElement, next: SummaryEdit): void {
  const rep = changedRange(textarea.value, next.value);
  textarea.focus();
  textarea.setSelectionRange(rep.start, rep.end);
  let applied = false;
  try {
    if (rep.start === rep.end && rep.text === "") {
      applied = true;
    } else if (rep.text === "") {
      applied = document.execCommand("delete", false);
    } else {
      applied = document.execCommand("insertText", false, rep.text);
    }
  } catch {
    applied = false;
  }
  if (applied && textarea.value !== next.value) applied = false;
  if (!applied) {
    textarea.setRangeText(rep.text, rep.start, rep.end, "end");
    textarea.dispatchEvent(new Event("input", { bubbles: true }));
  }
  textarea.setSelectionRange(next.selStart, next.selEnd);
}

export function toggleBold(value: string, selStart: number, selEnd: number): SummaryEdit {
  const [a, b] = order(value, selStart, selEnd);
  if (a !== b) {
    const wrapped = findBoldWrap(value, a, b);
    if (wrapped) return unwrapBold(value, wrapped);
  }
  if (a === b) return insertAt(value, a, "**bold text**", 2, 2 + "bold text".length);

  let innerStart = a;
  let innerEnd = b;
  while (innerStart < innerEnd && value[innerStart] === " ") innerStart += 1;
  while (innerEnd > innerStart && value[innerEnd - 1] === " ") innerEnd -= 1;
  if (innerStart === innerEnd) return insertAt(value, b, "**bold text**", 2, 2 + "bold text".length);

  const inner = value.slice(innerStart, innerEnd);
  const next = value.slice(0, innerStart) + "**" + inner + "**" + value.slice(innerEnd);
  return { value: next, selStart: innerStart + 2, selEnd: innerStart + 2 + inner.length };
}

export function toggleBulletList(value: string, selStart: number, selEnd: number): SummaryEdit {
  return toggleList(value, selStart, selEnd, "bullet");
}

export function toggleNumberedList(value: string, selStart: number, selEnd: number): SummaryEdit {
  return toggleList(value, selStart, selEnd, "number");
}

export function insertLink(value: string, selStart: number, selEnd: number): SummaryEdit {
  const [a, b] = order(value, selStart, selEnd);
  const selected = value.slice(a, b);
  if (a === b) {
    const label = "link text";
    const insert = `[${label}](https://)`;
    return insertAt(value, a, insert, 1, 1 + label.length);
  }
  if (isUrl(selected)) {
    const label = "link text";
    const insert = `[${label}](${selected})`;
    return insertAt(value, a, insert, 1, 1 + label.length, b);
  }
  const url = "https://";
  const insert = `[${selected}](${url})`;
  const urlStart = selected.length + 3;
  return insertAt(value, a, insert, urlStart, urlStart + url.length, b);
}

/**
 * Enter inside a list item. Returns null when the caret is not in an item,
 * so the textarea keeps its normal line break.
 */
export function continueList(value: string, selStart: number, selEnd: number): SummaryEdit | null {
  if (selStart !== selEnd) return null;
  const lines = value.split("\n");
  const located = locateLine(lines, selStart);
  if (!located) return null;
  const { index, offset } = located;
  const line = lines[index];
  const parsed = parseItem(line);
  if (!parsed) return null;
  // An empty item ends the list wherever the caret sits on that line.
  if (parsed.content.trim() === "") {
    const lineStart = lineOffset(lines, index);
    const next = value.slice(0, lineStart) + value.slice(lineStart + line.length);
    return { value: next, selStart: lineStart, selEnd: lineStart };
  }
  if (offset < parsed.contentStart) return null;

  const before = line.slice(0, offset);
  const after = line.slice(offset);
  const nextMarker = stepMarker(parsed.marker);
  const prefix = `${parsed.indent}${nextMarker} `;
  const lineStart = lineOffset(lines, index);
  const next =
    value.slice(0, lineStart) + before + "\n" + prefix + after + value.slice(lineStart + line.length);
  const caret = lineStart + before.length + 1 + prefix.length;
  return { value: next, selStart: caret, selEnd: caret };
}

/** Paste a single https URL over a selection as a named link. Null keeps the normal paste. */
export function pasteLink(
  value: string,
  selStart: number,
  selEnd: number,
  clipboard: string,
): SummaryEdit | null {
  const [a, b] = order(value, selStart, selEnd);
  if (a === b) return null;
  const url = clipboard.replace(/\r\n/g, "\n").trim();
  if (!/^https:\/\/\S+$/.test(url)) return null;
  const selected = value.slice(a, b);
  const insert = `[${selected}](${url})`;
  const next = value.slice(0, a) + insert + value.slice(b);
  const caret = a + insert.length;
  return { value: next, selStart: caret, selEnd: caret };
}

function toggleList(
  value: string,
  selStart: number,
  selEnd: number,
  kind: "bullet" | "number",
): SummaryEdit {
  const [a, b] = order(value, selStart, selEnd);
  const lines = value.split("\n");
  let { start, end } = lineSpan(lines, a, b);
  const selected = lines.slice(start, end + 1);
  const meaningful = selected.filter((line) => line.trim() !== "");
  const pool = meaningful.length > 0 ? meaningful : selected;

  if (kind === "bullet" && pool.length > 0 && pool.every((line) => BULLET_LINE.test(line))) {
    const nextLines = lines.map((line, i) => {
      if (i < start || i > end) return line;
      return stripBullet(line);
    });
    return coverLines(nextLines, start, end);
  }

  if (kind === "number" && pool.length > 0 && pool.every((line) => NUMBER_LINE.test(line))) {
    if (numberedInOrder(pool)) {
      const nextLines = lines.map((line, i) => {
        if (i < start || i > end) return line;
        return stripNumber(line);
      });
      return coverLines(nextLines, start, end);
    }
    const nextLines = lines.slice();
    let n = 0;
    for (let i = start; i <= end; i++) {
      if (nextLines[i].trim() === "") continue;
      n += 1;
      nextLines[i] = renumber(nextLines[i], n);
    }
    return coverLines(nextLines, start, end);
  }

  const above = start > 0 ? lines[start - 1] : "";
  const needsBlank = above.trim() !== "" && !LIST_LINE.test(above);
  const nextLines = lines.slice();
  if (kind === "bullet") {
    for (let i = start; i <= end; i++) nextLines[i] = toBullet(nextLines[i], meaningful.length === 0);
  } else {
    let n = 0;
    for (let i = start; i <= end; i++) {
      if (nextLines[i].trim() === "" && meaningful.length > 0) continue;
      n += 1;
      nextLines[i] = toNumber(nextLines[i], n, meaningful.length === 0);
    }
  }
  if (needsBlank) {
    nextLines.splice(start, 0, "");
    start += 1;
    end += 1;
  }
  return coverLines(nextLines, start, end);
}

function toBullet(line: string, emptyToo: boolean): string {
  if (line.trim() === "") return emptyToo ? "- " : line;
  const numbered = NUMBER_LINE.exec(line);
  if (numbered) return `${numbered[1]}- ${numbered[4]}`;
  if (BULLET_LINE.test(line)) return line;
  const indent = leadingIndent(line);
  return `${indent}- ${line.slice(indent.length)}`;
}

function toNumber(line: string, n: number, emptyToo: boolean): string {
  if (line.trim() === "") return emptyToo ? `${n}. ` : line;
  const indent = leadingIndent(line);
  const numbered = NUMBER_LINE.exec(line);
  if (numbered) return `${numbered[1]}${n}. ${numbered[4]}`;
  const bullet = BULLET_LINE.exec(line);
  if (bullet) return `${bullet[1]}${n}. ${bullet[2]}`;
  return `${indent}${n}. ${line.slice(indent.length)}`;
}

function renumber(line: string, n: number): string {
  const numbered = NUMBER_LINE.exec(line);
  if (!numbered) return toNumber(line, n, false);
  return `${numbered[1]}${n}. ${numbered[4]}`;
}

function stripBullet(line: string): string {
  const match = BULLET_LINE.exec(line);
  if (!match) return line;
  return `${match[1]}${match[2]}`;
}

function stripNumber(line: string): string {
  const match = NUMBER_LINE.exec(line);
  if (!match) return line;
  return `${match[1]}${match[4]}`;
}

function numberedInOrder(lines: string[]): boolean {
  let n = 0;
  for (const line of lines) {
    if (line.trim() === "") continue;
    const match = NUMBER_LINE.exec(line);
    if (!match || match[3] !== "." || Number(match[2]) !== n + 1) return false;
    n += 1;
  }
  return n > 0;
}

function coverLines(lines: string[], start: number, end: number): SummaryEdit {
  const value = lines.join("\n");
  let selStart = 0;
  for (let i = 0; i < start; i++) selStart += lines[i].length + 1;
  let selEnd = selStart;
  for (let i = start; i <= end; i++) {
    selEnd += lines[i].length;
    if (i < end) selEnd += 1;
  }
  return { value, selStart, selEnd };
}

function lineSpan(lines: string[], selStart: number, selEnd: number): { start: number; end: number } {
  const starts: number[] = [];
  let offset = 0;
  for (const line of lines) {
    starts.push(offset);
    offset += line.length + 1;
  }
  let start = 0;
  let end = 0;
  for (let i = 0; i < lines.length; i++) {
    if (starts[i] <= selStart) start = i;
    if (starts[i] <= selEnd) end = i;
  }
  if (selEnd > selStart && end > start && starts[end] === selEnd) end -= 1;
  return { start, end };
}

function lineOffset(lines: string[], index: number): number {
  let offset = 0;
  for (let i = 0; i < index; i++) offset += lines[i].length + 1;
  return offset;
}

function locateLine(lines: string[], caret: number): { index: number; offset: number } | null {
  let passed = 0;
  for (let i = 0; i < lines.length; i++) {
    const end = passed + lines[i].length;
    if (caret <= end) return { index: i, offset: caret - passed };
    passed = end + 1;
  }
  return null;
}

interface ParsedItem {
  indent: string;
  marker: string;
  contentStart: number;
  content: string;
}

function parseItem(line: string): ParsedItem | null {
  const match = /^(\s*)([-+*]|\d+[.)])(\s+)(.*)$/.exec(line);
  if (!match) return null;
  const indent = match[1];
  const marker = match[2];
  const gap = match[3];
  return {
    indent,
    marker,
    contentStart: indent.length + marker.length + 1,
    content: gap.slice(1) + match[4],
  };
}

function stepMarker(marker: string): string {
  const numbered = /^(\d+)([.)])$/.exec(marker);
  if (!numbered) return marker;
  return `${Number(numbered[1]) + 1}${numbered[2]}`;
}

function leadingIndent(line: string): string {
  return /^(\s*)/.exec(line)?.[1] ?? "";
}

function order(value: string, selStart: number, selEnd: number): [number, number] {
  const len = value.length;
  const a = clamp(Math.min(selStart, selEnd), len);
  const b = clamp(Math.max(selStart, selEnd), len);
  return [a, b];
}

function clamp(n: number, len: number): number {
  if (n < 0) return 0;
  if (n > len) return len;
  return n;
}

interface BoldWrap {
  start: number;
  end: number;
  inner: string;
}

function findBoldWrap(value: string, selStart: number, selEnd: number): BoldWrap | null {
  const selected = value.slice(selStart, selEnd);
  if (selected.startsWith("**") && selected.endsWith("**") && selected.length >= 4) {
    return { start: selStart, end: selEnd, inner: selected.slice(2, -2) };
  }
  let openAt = selStart;
  while (openAt > 0 && value[openAt - 1] === " ") openAt -= 1;
  if (value.slice(openAt - 2, openAt) !== "**") return null;
  let closeAt = selEnd;
  while (closeAt < value.length && value[closeAt] === " ") closeAt += 1;
  if (value.slice(closeAt, closeAt + 2) !== "**") return null;
  const start = openAt - 2;
  const end = closeAt + 2;
  return { start, end, inner: value.slice(start + 2, end - 2) };
}

function unwrapBold(value: string, wrap: BoldWrap): SummaryEdit {
  const next = value.slice(0, wrap.start) + wrap.inner + value.slice(wrap.end);
  return {
    value: next,
    selStart: wrap.start,
    selEnd: wrap.start + wrap.inner.length,
  };
}

function insertAt(
  value: string,
  at: number,
  insert: string,
  relStart: number,
  relEnd: number,
  replaceEnd = at,
): SummaryEdit {
  const next = value.slice(0, at) + insert + value.slice(replaceEnd);
  return { value: next, selStart: at + relStart, selEnd: at + relEnd };
}

function isUrl(text: string): boolean {
  return /^https?:\/\/\S+$/.test(text);
}
